import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Radio, Space, Typography } from 'antd';
import AnimateHeight from 'react-animate-height';

import selectors from '../../../../store/selectors';
import ServiceFee from '../../../ServiceFee/ServiceFee';
import { useMaybeFetchServiceFee } from '../../../../hooks/useMaybeFetchServiceFee';
import FormInputError from '../../../Form/FormInputError';
import OfferingAmount from '../../../Form/OfferingAmount/OfferingAmount';

import styles from './DeathPrayersForm.module.scss';

const DEATH_PRAYERS_TYPES_TO_NAME = {
  single: 'Listopadowe (jednorazowe)',
  yearly: 'Roczne',
  slot_1_1678739374494384128: 'Roczne (o 10:00)',
  slot_2_1678739374494384128: 'Roczne (o 12:00)',
  slot_1_1724194798657257472: 'Roczne (o 7:30)',
  slot_2_1724194798657257472: 'Roczne (o 9:30)',
  slot_3_1724194798657257472: 'Roczne (o 11:30)',
  slot_1_1518171333547393024: 'Roczne (odczytywane w 1 niedzielę miesiąca)',
  slot_2_1518171333547393024: 'Roczne (odczytywane w 2 niedzielę miesiąca)',
  slot_3_1518171333547393024: 'Roczne (odczytywane w 3 niedzielę miesiąca)',
  slot_4_1518171333547393024: 'Roczne (odczytywane w 4 niedzielę miesiąca)',
  slot_1_1657776477594714112:
    '04 XI (wt): Księginice, Ogonowice, Lubień, Biskupice, Raczkowa, Pawłowice Wielkie',
  slot_2_1657776477594714112:
    '05 XI (śr): Kossak-Szuckiej, 9 kwietnia, Bielowskiego, H. Pobożnego, H. Brodatego, Księżnej Anny, Książąt Śl., Bajdara',
  slot_3_1657776477594714112:
    '06 XI (czw): Gniewomierz - godz. 16.30 (kościół św. Antoniego Padewskiego)',
  slot_4_1657776477594714112:
    '07 XI (pt): Asama, Wyspiańskiego, Piastowska, Klasztorna, Długosza, Matejki, Dientzenhofera, św. Barbary',
  slot_5_1657776477594714112: '08 XI (sob): św. Jadwigi, Turecka, Janczarska i Strachowice',
  slot_6_1657776477594714112:
    '12 XI (śr): Benedyktynów, Fredry, Rycerska, Słowackiego, Sienkiewicza, Tatarska, Mickiewicza, Norwida, Krasińskiego',
  slot_7_1657776477594714112:
    '13 XI (czw): Mikołajowice - godz. 16.30 (kościół MB Częstochowskiej)',
  slot_8_1657776477594714112:
    '14 XI (pt): Księginice, Ogonowice, Lubień, Biskupice, Raczkowa, Pawłowice Wielkie',
  slot_9_1657776477594714112:
    '15 XI (sob): Kossak-Szuckiej, 9 kwietnia, Bielowskiego, H. Pobożnego, H, Brodatego, Księżnej Anny, Książąt Śl., Bajdara',
  slot_10_1657776477594714112:
    '18 XI (wt) Asama, Wyspiańskiego, Piastowska, Klasztorna, Długosza, Matejki, Dientzenhofera, św. Barbary',
  slot_11_1657776477594714112: '19 XI (śr): św. Jadwigi, Turecka, Janczarska i Strachowice',
  slot_12_1657776477594714112:
    '20 XI (czw): Benedyktynów, Fredry, Rycerska, Słowackiego, Sienkiewicza, Tatarska, Mickiewicza, Norwida, Krasińskiego',
  slot_13_1657776477594714112: '21 XI (pt): wypominki bez podanych ulic i miejscowości',
};

const DeathPrayersForm = () => {
  const deathPrayersTypes = useSelector(selectors.getDeathPrayersTypes);
  const parishData = useSelector(selectors.getAccountData);
  const isPhoneFieldAvailable = useSelector(selectors.isDeathPrayersPhoneFieldAvailable);
  const deathPrayersAmount = useSelector(selectors.selectDeathPrayersAmountOption);

  const minimumAmount = deathPrayersAmount?.minimalValue || 1;
  const [offeringAmountError, setOfferingAmountError] = useState(null);
  const [showDeliveryAddress, setShowDeliveryAddress] = useState(false);

  const isServiceFeeEnabled = useSelector(selectors.selectIsServiceFeeEnabled);
  const isServiceFeeSelectedCoveredDefault = useSelector(
    selectors.selectIsServiceFeeCoveredDefault,
  );
  const isServiceFeeCoveredRequired = useSelector(selectors.selectIsServiceFeeCoveredRequired);
  const [offeringAmount, setOfferingAmount] = useState(
    deathPrayersAmount?.predefinedValues?.[1] || 0,
  );
  const [totalAmountToPay, setTotalAmountToPay] = useState(
    deathPrayersAmount?.predefinedValues?.[1] || 0,
  );
  const [serviceFeeChecked, setServiceFeeChecked] = useState(
    isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault,
  );
  const { loading: serviceFeeLoading } = useMaybeFetchServiceFee({
    isServiceFeeEnabled,
    isServiceFeeSelectedCoveredDefault:
      isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault,
    offeringAmount,
    setTotalAmountToPay: value => {
      setTotalAmountToPay(value);
    },
    userCoversServiceFee: serviceFeeChecked,
  });

  const registerServiceFeeInput = useMemo(() => {
    return () => {
      return {
        onChange: event => {
          setServiceFeeChecked(event.target.checked);
        },
      };
    };
  }, []);

  return (
    <form autoComplete="on" method="post" name="death_prayers_form" id="death_prayers_form">
      <Space direction="vertical" size="large">
        <div>
          <div className="form-group">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <textarea
              className="form-control"
              placeholder="np. Jan Kowalski&#10;Irena Nowak"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              required="required"
              name="death_prayers_form[names_of_death]"
              id="names_of_death"
              rows="7"
            />
          </div>

          {deathPrayersTypes.length > 1 ? (
            <div className="form-group">
              <Space direction="vertical">
                <span>Rodzaj wypominek:</span>

                <Radio.Group name="death_prayers_form[type]" defaultValue={deathPrayersTypes[0]}>
                  <Space direction="vertical" className="ml-3">
                    {deathPrayersTypes.map(item => {
                      return (
                        <Radio value={item} key={item}>
                          {DEATH_PRAYERS_TYPES_TO_NAME[item]}
                        </Radio>
                      );
                    })}
                  </Space>
                </Radio.Group>
              </Space>
            </div>
          ) : (
            <input type="hidden" name="death_prayers_form[type]" value={deathPrayersTypes[0]} />
          )}
        </div>

        <div>
          <Typography.Title level={4} className="mb-3">
            Dane zamawiającego
          </Typography.Title>

          <div className="md-form input-group">
            <input
              className="form-control"
              id="death_prayers_form_name"
              name="death_prayers_form[name]"
              type="text"
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="death_prayers_form_name">Imię i nazwisko (opcjonalnie)</label>
          </div>

          <div className="md-form input-group">
            <input
              className="form-control"
              id="death_prayers_form_email"
              name="death_prayers_form[email]"
              required="required"
              type="email"
              autoComplete="email"
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="death_prayers_form_email">E-mail</label>
          </div>

          {isPhoneFieldAvailable && (
            <div className="md-form input-group">
              <input
                className="form-control"
                id="death_prayers_form_phone"
                name="death_prayers_form[phone]"
                type="tel"
                autoComplete="tel"
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="death_prayers_form_phone">Numer telefonu (opcjonalnie)</label>
            </div>
          )}

          <div className="md-form input-group mt-0 mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="death_prayers_form_show_delivery_address"
              onChange={e => setShowDeliveryAddress(e.target.checked)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="death_prayers_form_show_delivery_address">
              Chcę podać adres zamieszkania
            </label>
          </div>

          <AnimateHeight duration={500} height={showDeliveryAddress ? 'auto' : 0}>
            <div>
              <div className="md-form input-group">
                <input
                  className="form-control"
                  id="death_prayers_form_delivery_street"
                  name="death_prayers_form[delivery_address][street]"
                  type="text"
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="death_prayers_form_delivery_street">Ulica i numer</label>
              </div>

              <div className="md-form input-group">
                <input
                  className="form-control"
                  id="death_prayers_form_delivery_zip_code"
                  name="death_prayers_form[delivery_address][zip_code]"
                  type="text"
                  pattern="[0-9]{2}-[0-9]{3}"
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="death_prayers_form_delivery_zip_code">Kod pocztowy</label>
              </div>

              <div className="md-form input-group">
                <input
                  className="form-control"
                  id="death_prayers_form_delivery_city"
                  name="death_prayers_form[delivery_address][city]"
                  type="text"
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="death_prayers_form_delivery_city">Miasto</label>
              </div>

              <div className="md-form input-group">
                <input
                  className="form-control"
                  id="death_prayers_form_delivery_country"
                  name="death_prayers_form[delivery_address][country]"
                  type="text"
                  defaultValue="Polska"
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="death_prayers_form_delivery_country" className="active">
                  Kraj
                </label>
              </div>
            </div>
          </AnimateHeight>

          <div className="md-form input-group mt-0">
            <input
              className="form-check-input"
              id="death_prayers_form_agreement"
              name="death_prayers_form[agreement]"
              required="required"
              type="checkbox"
              value="1"
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="form-check-label w-100" htmlFor="death_prayers_form_agreement">
              Akceptuję <a href={parishData.getRegulations}>Regulamin</a>
            </label>
          </div>
        </div>

        <div>
          <Typography.Title level={4} className="mb-3">
            Ofiara
          </Typography.Title>

          <div className="md-form input-group">
            <OfferingAmount
              allowFreeOfferings={deathPrayersAmount?.allowOwnAmount || false}
              clearErrors={() => setOfferingAmountError(null)}
              massOfferingAmount={deathPrayersAmount}
              minimalMassOfferingAmount={minimumAmount}
              name="death_prayers_form[offering_amount]"
              onChange={newValue => {
                setOfferingAmount(newValue);
                setOfferingAmountError(null);
              }}
              setError={(name, error) => {
                setOfferingAmountError(error.message);
              }}
              value={offeringAmount}
              showLabel
              optionalFreeOfferingsMessage="Aby złożyć niższą ofiarę skontaktuj się bezpośrednio z kancelarią parafialną."
            />
            <input
              value={
                // eslint-disable-next-line no-nested-ternary
                Number.isNaN(totalAmountToPay)
                  ? ''
                  : totalAmountToPay > 0
                  ? totalAmountToPay
                  : offeringAmount
              }
              name="death_prayers_form[amount]"
              required="required"
              type="hidden"
            />

            {offeringAmountError && <FormInputError errorText={offeringAmountError} />}
          </div>

          {isServiceFeeEnabled && (
            <div className={classNames(styles.serviceFeeWrapper, 'md-form input-group')}>
              <ServiceFee
                inputFieldName="death_prayers_form[service_fee_checked]"
                isRequired={isServiceFeeCoveredRequired}
                offeringAmount={offeringAmount}
                register={registerServiceFeeInput}
                serviceFeeLoading={serviceFeeLoading}
                totalAmountToPay={totalAmountToPay}
              />
            </div>
          )}
        </div>

        <div>
          <div className="md-form input-group mt-0">
            <p className="grey-text">
              <small>
                <em>
                  Po kliknięciu przycisku &ldquo;Zamawiam wypominki&rdquo; zostaniesz przekierowany
                  do operatora płatności, gdzie sfinalizujesz ofiarę.
                </em>
              </small>
            </p>
            <button
              className="btn btn-outline-info btn-rounded btn-block z-depth-0 mt-0 mb-4 waves-effect submit-btn"
              id="death_prayers_form_save"
              name="death_prayers_form[save]"
              type="submit"
            >
              Zamawiam wypominki
            </button>
          </div>
        </div>
      </Space>

      <input name="death_prayers_form[_token]" type="hidden" value={parishData.csrf} />
    </form>
  );
};

DeathPrayersForm.propTypes = {};

export default DeathPrayersForm;
